import SelectField from "components/core/basic/SelectField";
import Loading from "components/core/blocks/Loading";
import { useFetch } from "hooks/api";
import { useMemo } from "react";
import { Col, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "utils/api";
import { successToast, errorToast } from "utils/toasts";
import { Button } from "../../components/core/basic/Button";

const ControlForm = ({ defaultLineItem, onSubmit, programs }) => {
  const { handleSubmit, control } = useForm({
    shouldUnregister: false,
    defaultValues: defaultLineItem,
  });
  const programOptions = useMemo(() => {
    const options =
      programs?.map((program) => ({
        label: program.name,
        value: program.id,
      })) ?? [];
    options.unshift({ label: "No Bidding Program", value: -1 });
    return options;
  }, [programs]);
  return (
    <Col xs={12} lg={6} className="fs-stantard">
      <Row>
        <Col xs={12}>
          <h5 className="fw-bold pb-2 pt-1">Line Item Bidding Rules</h5>
          <Controller
            name="program"
            control={control}
            render={({ field }) => (
              <div className="flex-fill pe-3">
                <SelectField {...field} value={field.value || -1} options={programOptions} />
              </div>
            )}
          />
        </Col>
        <Col xs={12}>
          <p className="fs-5 mt-3">
            Use Line Item Rules to control the bids for this line item based on performance.
            <br />
            Click here to create new <Link to="/user/advertising/settings">Line Item Rules</Link>.
          </p>
        </Col>
        <Col xs={12} className="d-flex justify-content-end mt-3">
          <Button
            variant="primary"
            onPress={() => {
              handleSubmit(onSubmit)();
            }}
          >
            Save
          </Button>
        </Col>
      </Row>
    </Col>
  );
};

const DSPLineItemAutomation = ({ lineItemId }) => {
  const { data: lineItemControlData, loading: isLineItemControlDataLoading } = useFetch(
    ["lineItemControl", lineItemId],
    `/dsp/lineitemcontrol/?line_item=${lineItemId}`,
    {},
    {
      select: (data) => {
        if (data.data?.results?.length === 0) {
          return {
            line_item: lineItemId,
            program: null,
          };
        }
        return data.data?.results?.[0];
      },
      enabled: !!lineItemId,
    },
  );

  const { data: programsData, loading: isProgramsDataLoading } = useFetch(
    ["programs", "lineitembidding"],
    "/programs/?engine=lineitem_bidding",
    {},
    {
      select: (data) => data?.data?.results ?? [],
    },
  );

  const queryClient = useQueryClient();

  const createControl = useMutation({
    mutationFn: async (data) => {
      if (data.program === -1) {
        data.program = null;
      }
      await api.post("/dsp/lineitemcontrol/", data);
    },

    onMutate: () => {},

    onSuccess: () => {
      successToast({
        message: "Updated Line Item",
      });
      queryClient.invalidateQueries({
        queryKey: ["lineItemControl"],
      });
    },

    onError: (error) => {
      errorToast({
        message: `Failed create to Line Item`,
        error,
      });
    },
  });

  const updateControl = useMutation({
    mutationFn: async (data) => {
      if (data.program === -1) {
        data.program = null;
      }
      await api.patch(`/dsp/lineitemcontrol/${lineItemControlData.id}/`, data);
    },

    onMutate: () => {},

    onSuccess: () => {
      successToast({
        message: "Updated Line Item",
      });
      queryClient.invalidateQueries({
        queryKey: ["lineItemControl"],
      });
    },

    onError: (error) => {
      errorToast({
        message: `Failed to update Line Item`,
        error,
      });
    },
  });

  return isLineItemControlDataLoading || isProgramsDataLoading ?
      <Loading />
    : lineItemControlData && (
        <ControlForm
          programs={programsData}
          defaultLineItem={lineItemControlData}
          onSubmit={lineItemControlData?.id ? updateControl.mutate : createControl.mutate}
        />
      );
};

export default DSPLineItemAutomation;
